/**
 * 数值条件格式纯规则：图标分组、左/右展示位。
 *
 * @see constants.ts · types.ts
 */

import {
  DEFAULT_NUMERICAL_ICON,
  NUMERICAL_ICON_GROUPS,
  NUMERICAL_ICON_NONE,
} from '../constants';

/**
 * 根据 icon type 反查所属分组 key（change | sentiment | others）。
 *
 * @core 面板 Tab 与 splitNumericalIconByPlacement 左右位判定共用分组。
 */
export const getNumericalIconGroupKey = (
  iconType: string,
): string | undefined =>
  NUMERICAL_ICON_GROUPS.find(group => group.icons.includes(iconType))?.key;

/**
 * 规范 icon 字段：none 表示不展示；空值回退默认图标；否则原样使用 iconfont type。
 *
 * @core ChartSettingsV2 存库与 AliTableAnalytics 渲染读同一规范化规则。
 */
export const normalizeNumericalIconValue = (
  icon?: string,
  defaultIcon: string = DEFAULT_NUMERICAL_ICON,
): string => {
  if (icon === NUMERICAL_ICON_NONE) {
    return NUMERICAL_ICON_NONE;
  }
  if (!icon) {
    return defaultIcon;
  }
  return icon;
};

export const isNumericalIconNone = (icon?: string): boolean =>
  icon === NUMERICAL_ICON_NONE;

/**
 * 把单个 icon 拆成左/右展示位。
 *
 * - change 组（涨跌箭头、减号）：在数值 **右侧**
 * - sentiment / others（表情、点赞）：在数值 **左侧**
 *
 * @core 面板选 icon 与 NumericalValueContent 单元格布局须一致；放组件内无法双向对齐。
 */
export const splitNumericalIconByPlacement = (
  icon?: string,
): { left?: string; right?: string } => {
  const normalized = normalizeNumericalIconValue(icon, NUMERICAL_ICON_NONE);
  if (isNumericalIconNone(normalized)) {
    return {};
  }
  const groupKey = getNumericalIconGroupKey(normalized);
  if (groupKey === 'change') {
    return { right: normalized };
  }
  return { left: normalized };
};
